/**
 * PaywallCard — the atomic "you hit a limit" card (Plan 02 D1): what is
 * blocked, what the top-up gives, one CTA (opens the consumer's
 * UpgradeSheet). Variants: inline (card), modal (fills a dialog body),
 * inline-banner (single-row banner). Apps never implement paywall logic —
 * they pass the snapshot fact and the CTA target.
 */
import type { ReactNode } from "react";
import { useBillingLabels } from "../strings";

export type PaywallVariant = "inline" | "modal" | "inline-banner";

export interface PaywallCardProps {
  /** The service in human words ("Storage", "AI"). */
  service: string;
  /** The limit the caller crossed — rendered when provided. */
  blockedLimit?: string | null;
  variant?: PaywallVariant;
  /** Override the default body copy when the blocking fact needs its own
   *  words (a per-file cap is not a consumed allowance). */
  body?: ReactNode;
  /** The one CTA — opens the UpgradeSheet. Omit for a display-only card. */
  onUpgrade?: () => void;
}

export function PaywallCard({ service, blockedLimit, variant = "inline", body, onUpgrade }: PaywallCardProps) {
  const labels = useBillingLabels();
  const className =
    variant === "inline-banner"
      ? "suite-billing-paywall suite-billing-paywall--banner"
      : variant === "modal"
        ? "suite-billing-paywall suite-billing-paywall--modal"
        : "suite-billing-paywall";

  if (variant === "inline-banner") {
    return (
      <div className={className}>
        <p className="suite-billing-paywall-body">{labels.paywallTitle(service)}</p>
        {onUpgrade && (
          <button type="button" className="suite-billing-button suite-billing-button--primary" onClick={onUpgrade}>
            {labels.upgradeCta}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="suite-billing-paywall-title">{labels.paywallTitle(service)}</p>
      <p className="suite-billing-paywall-body">
        {body ?? (
          <>
            {labels.paywallBody(service)}
            {blockedLimit ? ` ${blockedLimit}` : ""}
          </>
        )}
      </p>
      {onUpgrade && (
        <div>
          <button type="button" className="suite-billing-button suite-billing-button--primary" onClick={onUpgrade}>
            {labels.upgradeCta}
          </button>
        </div>
      )}
    </div>
  );
}
